/**
 * 配達希望日・時間帯 (GRAPHIQUE NOIR)
 * - カート画面で選んでもらい、注文の「追加の詳細」(cart attributes) に記録する
 *   (チェックアウト画面に入力欄を足せるのは Shopify Plus だけなので、カート画面で受ける)
 * - 同じ内容を、カートの各商品の情報 (line item properties) にも写す。
 *   cart attributes はお客さんの画面には出ないが、商品の情報はチェックアウトの注文サマリー・注文状況ページ・
 *   注文確認メールに「発送予定日」と並んで出るので、「指定したのに出ていない」と迷わせないため。
 *   正は cart attributes。商品の情報は写しで、ずれていたら attributes に合わせ直す
 * - 選べる日付は、発送予定日の計算 (assets/gn-ship-date.js) に連動させる:
 *   カートの中でいちばん遅い発送予定日 + minDays 日後 から rangeDays 日分
 * - 時間が経ったり予約商品を足したりして、選んだ日付が間に合わなくなったら「指定なし」に戻す。
 *   カート画面以外のページでも確認する (追加直後のお知らせから、そのまま購入手続きへ進めるため)
 * - 設定は snippets/gn-delivery-date-config.liquid (テーマ設定 > GN: 配達希望日時)
 */
(() => {
  const DAY_MS = 86400000;
  const FETCH_TIMEOUT_MS = 10000;
  const SHIP_FROM_PROPERTY = '_gn_ship_from';
  const MAX_LINE_UPDATES = 20;

  const readJson = (id) => {
    const element = document.getElementById(id);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(`[gn-delivery-date] ${id} の読み込みに失敗しました`, error);
      return null;
    }
  };

  const requestJson = (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    return fetch(url, { ...options, signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .finally(() => clearTimeout(timer));
  };

  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const parseFull = (text) => {
    const match = /(\d{4})年(\d{1,2})月(\d{1,2})日/.exec(text || '');
    return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))) : null;
  };

  // カートの中でいちばん遅い発送予定日を求める。計算できないときは null (= 日付は選ばせない)
  const latestShipDay = (cart, shipConfig) => {
    const api = window.GnShipDate;
    if (!api || !shipConfig) return null;
    let latest = null;
    for (const item of cart.items || []) {
      if (item.requires_shipping === false) continue;
      const shipFrom = api.parseIsoDay((item.properties || {})[SHIP_FROM_PROPERTY]);
      const { shipDay } = api.computeShipDate(new Date(), shipConfig, shipFrom);
      if (!latest || shipDay.getTime() > latest.getTime()) latest = shipDay;
    }
    return latest;
  };

  const earliestDeliveryDay = (cart, shipConfig, config) => {
    const shipDay = latestShipDay(cart, shipConfig);
    return shipDay ? new Date(shipDay.getTime() + config.minDays * DAY_MS) : null;
  };

  const saveAttributes = (attributes) =>
    requestJson(`${cartRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });

  // 商品の情報 (line item properties) を、希望日時に合わせた形にしたもの。空の希望は項目ごと消す
  const wantedProperties = (item, config, date, time) => {
    const properties = { ...(item.properties || {}) };
    delete properties[config.dateName];
    delete properties[config.timeName];
    if (date) properties[config.dateName] = date;
    if (time) properties[config.timeName] = time;
    return properties;
  };

  const sameProperties = (a, b) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
  };

  // 希望日時を、配送が必要なすべての商品の情報に写す。戻り値: カートを書き換えたか
  const syncLines = async (cart, config) => {
    const attributes = cart.attributes || {};
    const date = attributes[config.dateName] || '';
    const time = attributes[config.timeName] || '';
    let current = cart;
    let changed = false;
    for (let i = 0; i < MAX_LINE_UPDATES; i += 1) {
      const stale = (current.items || []).find(
        (item) => item.requires_shipping !== false && !sameProperties(item.properties || {}, wantedProperties(item, config, date, time))
      );
      if (!stale) break;
      // properties を変えると行の key が変わるため、毎回レスポンスのカートから探し直す
      current = await requestJson(`${cartRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: stale.key, quantity: stale.quantity, properties: wantedProperties(stale, config, date, time) }),
      });
      changed = true;
    }
    return changed;
  };

  // カート画面の商品一覧を描き直す (商品の情報を書き換えたあと)
  const rerenderCartItems = () => {
    const cartItems = document.querySelector('cart-items, cart-drawer-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') return cartItems.onCartUpdate();
    return null;
  };

  const readConfigs = () => {
    const raw = readJson('GnDeliveryDateConfig');
    const shipRaw = readJson('GnShipDateConfig');
    if (!raw || !shipRaw || !window.GnShipDate) return null;
    return {
      config: {
        dateName: raw.dateName,
        timeName: raw.timeName,
        minDays: Math.max(0, Number(raw.minDays) || 0),
        rangeDays: Math.max(1, Number(raw.rangeDays) || 14),
      },
      shipConfig: window.GnShipDate.normalizeConfig(shipRaw),
    };
  };

  class GnDeliveryDate extends HTMLElement {
    connectedCallback() {
      this.dateSelect = this.querySelector('[data-gn-delivery-date]');
      this.timeSelect = this.querySelector('[data-gn-delivery-time]');
      this.notice = this.querySelector('[data-gn-delivery-notice]');
      this.earliestText = this.querySelector('[data-gn-delivery-earliest]');
      const configs = readConfigs();
      if (!configs || !this.dateSelect) return; // 計算できないときは、Liquid が出した「指定なし」だけの欄のままにする
      this.config = configs.config;
      this.shipConfig = configs.shipConfig;

      this.dateSelect.addEventListener('change', () => this.save());
      if (this.timeSelect) this.timeSelect.addEventListener('change', () => this.save());

      // 数量の変更や削除で、いちばん遅い発送予定日が変わることがある
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      this.refresh();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async refresh() {
      try {
        const cart = await requestJson(`${cartRoot()}cart.js`);
        const earliest = earliestDeliveryDay(cart, this.shipConfig, this.config);
        if (!earliest) return;
        const current = (cart.attributes || {})[this.config.dateName] || '';
        const valid = this.buildOptions(earliest, current);
        if (this.earliestText) this.earliestText.textContent = window.GnShipDate.formatShort(earliest);
        let latest = cart;
        if (current && !valid) {
          latest = await saveAttributes({ [this.config.dateName]: '' });
          this.showNotice(this.dataset.resetText);
        }
        if (await syncLines(latest, this.config)) await rerenderCartItems();
      } catch (error) {
        // 失敗しても購入は妨げない (希望日が選べないだけ)
        console.error('[gn-delivery-date] 配達希望日の候補を作れませんでした', error);
      }
    }

    // 戻り値: いま保存されている日付が、候補の中にあったか
    buildOptions(earliest, current) {
      const select = this.dateSelect;
      const none = select.querySelector('option[value=""]');
      select.replaceChildren(none);
      let found = false;
      for (let i = 0; i < this.config.rangeDays; i += 1) {
        const day = new Date(earliest.getTime() + i * DAY_MS);
        const option = document.createElement('option');
        option.value = window.GnShipDate.formatFull(day);
        option.textContent = window.GnShipDate.formatShort(day);
        if (option.value === current) {
          option.selected = true;
          found = true;
        }
        select.appendChild(option);
      }
      if (!found) none.selected = true;
      return found;
    }

    // 日付と時間帯を続けて選ぶと保存が同時に走り、後から届いた古い内容で上書きされることがある。
    // 1 つずつ順番に送り、送る直前の画面の値を使う
    save() {
      this.saving = (this.saving || Promise.resolve()).then(async () => {
        const attributes = { [this.config.dateName]: this.dateSelect.value };
        if (this.timeSelect) attributes[this.config.timeName] = this.timeSelect.value;
        try {
          const cart = await saveAttributes(attributes);
          if (await syncLines(cart, this.config)) await rerenderCartItems();
          this.showNotice('');
        } catch (error) {
          console.error('[gn-delivery-date] 配達希望日時を保存できませんでした', error);
          this.showNotice(this.dataset.errorText);
        }
      });
      return this.saving;
    }

    showNotice(text) {
      if (!this.notice) return;
      this.notice.textContent = text || '';
      this.notice.hidden = !text;
    }
  }

  if (!customElements.get('gn-delivery-date')) customElements.define('gn-delivery-date', GnDeliveryDate);

  // カート画面以外: 保存済みの希望日が間に合わなくなっていたら消す
  const validateSaved = async () => {
    const configs = readConfigs();
    if (!configs) return;
    let cart = await requestJson(`${cartRoot()}cart.js`);
    const saved = parseFull((cart.attributes || {})[configs.config.dateName]);
    if (saved) {
      const earliest = earliestDeliveryDay(cart, configs.shipConfig, configs.config);
      if (cart.item_count === 0 || (earliest && saved.getTime() < earliest.getTime())) {
        cart = await saveAttributes({ [configs.config.dateName]: '' });
      }
    }
    // あとから足した商品にも希望日時を写す (写っていないと、チェックアウトでその商品にだけ出ない)
    await syncLines(cart, configs.config);
  };

  const raw = readJson('GnDeliveryDateConfig');
  if (raw && (raw.savedDate || raw.savedTime) && !document.querySelector('gn-delivery-date')) {
    const run = () =>
      validateSaved().catch((error) => console.error('[gn-delivery-date] 保存済みの配達希望日を確認できませんでした', error));
    run();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, run);
  }
})();
